import { FC, useMemo, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { StudentClasses } from '../../../models/StudentClasses';
import { NO_ASSIGNED_ROADMAP } from '../ClassesRosterRightSide';
import { MaskedStudentData } from '../../MaskedData/MaskedStudentData';
import { StudentNameLink } from '../../common/StudentNameLink/StudentNameLink';
import { PiiTypes } from '../../../analytics/dataMasking';
import { ClassUser } from '../../../models/Class';
import ClassWizardNewStudentDialog from '../ClassesRosterWizard/ClassWizardNewStudentDialog';
import { SquarePen } from 'lucide-react';

interface Prop {
  studentsList: StudentClasses[];
  userOwnerOfClass?: boolean;
  setRefetchStudentKey?: (key: number) => void;
  setRefetchSingleClassKey: (key: number) => void;
  /** Full user records for the class roster, used to look up a row's raw data for editing. */
  classUsers?: ClassUser[];
  isClassExternal?: boolean;
  classId?: string;
  /** Supplied when the page owns the search box, which hides the one below. */
  searchTerm?: string;
}

const ClassesRosterStudentDataTable: FC<Prop> = ({
  studentsList,
  userOwnerOfClass,
  setRefetchStudentKey,
  setRefetchSingleClassKey,
  classUsers = [],
  isClassExternal,
  classId,
  searchTerm,
}) => {
  const [editingStudent, setEditingStudent] = useState<ClassUser | null>(null);
  const [ownSearch, setOwnSearch] = useState('');
  const isControlled = searchTerm !== undefined;
  const search = isControlled ? searchTerm : ownSearch;
  const toast = useRef<Toast>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return studentsList;
    return studentsList.filter(student =>
      `${student.studentFirstName} ${student.studentLastName}`.toLowerCase().includes(term),
    );
  }, [studentsList, search]);

  return (
    <>
      <Toast ref={toast} />
      {!isControlled && (
        <IconField iconPosition="left" className="nc_classes-search w-full mb-3">
          <InputIcon className="nc_icon-nc_search icon-sm"> </InputIcon>
          <InputText
            value={ownSearch}
            onChange={event => setOwnSearch(event.target.value)}
            placeholder="Search students..."
            className="w-full"
            aria-label="Search students"
          />
        </IconField>
      )}
      <DataTable
        paginator
        rows={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        value={filtered}
        emptyMessage="No students found."
        data-testid="datatable-student"
        stripedRows
        scrollable
        scrollHeight="600px"
        sortField="studentLastName"
        sortOrder={1}
      >
        <Column
          field="studentFirstName"
          header="First Name"
          sortable
          body={(student: StudentClasses) => (
            <StudentNameLink studentId={String(student.studentId)} classIds={classId ? [classId] : undefined}>
              <MaskedStudentData type={PiiTypes.FirstName} student={student}>
                {student.studentFirstName}
              </MaskedStudentData>
            </StudentNameLink>
          )}
        />
        <Column
          field="studentLastName"
          header="Last Name"
          sortable
          body={(student: StudentClasses) => (
            <StudentNameLink studentId={String(student.studentId)} classIds={classId ? [classId] : undefined}>
              <MaskedStudentData type={PiiTypes.LastName} student={student}>
                {student.studentLastName}
              </MaskedStudentData>
            </StudentNameLink>
          )}
        />
        <Column field="gradeName" header="Grade" sortable />
        <Column
          field="currentRoadmapName"
          header="Roadmap"
          sortable
          body={(item: StudentClasses) =>
            item.currentRoadmapName === NO_ASSIGNED_ROADMAP ? null : <span>{item.currentRoadmapName}</span>
          }
        />
        {userOwnerOfClass && (
          <Column
            body={(student: StudentClasses) => {
              const classUser = classUsers.find(user => user.id === student.studentId);
              if (!classUser) return null;
              return (
                <Button
                  icon={<SquarePen size={18} aria-hidden style={{ transform: 'translate(2px, 0.5px)' }} />}
                  outlined
                  rounded
                  style={{
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    borderWidth: '1.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label={`Edit ${student.studentFirstName} ${student.studentLastName}`}
                  onClick={() => setEditingStudent(classUser)}
                />
              );
            }}
          />
        )}
      </DataTable>
      {editingStudent && (
        <ClassWizardNewStudentDialog
          visible
          onHide={() => setEditingStudent(null)}
          siteId={editingStudent.sites?.[0]?.id ?? ''}
          siteName={editingStudent.sites?.[0]?.name ?? ''}
          student={editingStudent}
          isExternal={isClassExternal || Boolean(editingStudent.externalSync?.source)}
          toast={toast}
          onSaved={() => {
            setRefetchStudentKey?.(new Date().getTime());
            setRefetchSingleClassKey(new Date().getTime());
          }}
        />
      )}
    </>
  );
};

export default ClassesRosterStudentDataTable;
